// A state colour that WINS, instead of tinting.
//
// THE BUG (owner, 2026-09-20): "when I highlight/select a fragment, not all
// elements change material colour - some stay black or turn black instead
// of green."
//
// Every path multiplies. three's own color_vertex chunk is
//     vColor = vec3(1.0);  vColor *= color;  vColor.xyz *= instanceColor.xyz;
// where `color` is the element's COLOR_0 vertex colour - its real colour
// from Revit. So the highlight is a FACTOR on the element's own colour: a
// black element multiplies to black and can never show green, a mid-dark
// one goes muddy, a pale one tints correctly. It is not random and it is
// not per-path; the element's own colour decides whether the highlight is
// visible at all.
//
// So a selection / status / highlight colour must be the FINAL colour, not
// a modifier - green is green on a black pipe and on a white wall.
//
// HOW IT IS MARKED, and why there is no new attribute: all four paths
// already carry a per-element rgb, and none has a channel free on all four
// (merged tiles spend alpha on visibility, instanceColor is rgb only, and a
// new attribute costs 4 bytes per instance - about 5 MB on SpaceX - plus a
// buffer on every pool). A tint is always 0..1, so a value above 1.5 cannot
// occur naturally: writing rgb + 2.0 means "this IS the colour".
//
// This works only because every one of those channels is a 32-bit FLOAT and
// none is normalised - checked explicitly, because a byte or a normalised
// attribute would clamp 2.0 to 1.0, isFinal would never be true, and the
// element would multiply exactly as it does today. Silent, and identical to
// the bug being fixed:
//     pools         GlobalInstancePool.js  Float32Array -> InstancedBufferAttribute
//     batches       batchColor.js          DataTexture(..., RGBAFormat, FloatType)
//     merged tiles  tileMerge.js           Float32Array + FloatType texture
//     legacy        three's setColorAt     new Float32Array(count * 3)
export const FINAL_OFFSET = 2.0;
// Anything above this is a marked colour. Halfway between the highest a
// real tint can reach (1.0) and the lowest a marked one can (2.0).
export const FINAL_THRESHOLD = 1.5;

/** Mark a colour as final, in place. `out` is a three Color. */
export function markFinal(out) {
  out.r += FINAL_OFFSET;
  out.g += FINAL_OFFSET;
  out.b += FINAL_OFFSET;
  return out;
}

/** Is this stored red channel a marked colour? For tests and readback. */
export const isFinalEncoded = (r) => r > FINAL_THRESHOLD;

/** Undo the mark, for anything that reads a colour back out. */
export function decodeFinal(out) {
  out.r -= FINAL_OFFSET;
  out.g -= FINAL_OFFSET;
  out.b -= FINAL_OFFSET;
  return out;
}

// The one rule, as GLSL. Every path includes THIS rather than writing its
// own, so the four cannot drift apart on the encoding - which is the thing
// that would otherwise rot quietly, because three of them would keep
// working while the fourth stopped.
export const GLSL_APPLY_STATE_COLOR = `
vec3 blApplyStateColor( vec3 base, vec3 tint ) {
  // Above the threshold the tint IS the colour: a state colour must look
  // the same on a black pipe and a white wall.
  if ( tint.r > ${FINAL_THRESHOLD.toFixed(1)} ) return tint - ${FINAL_OFFSET.toFixed(1)};
  return base * tint;
}
`;

// three's color_vertex chunk, with the instancing multiply replaced by the
// rule above. Used to patch pooled and legacy InstancedMesh materials,
// which go through three's built-in instanceColor.
export const COLOR_VERTEX_WITH_STATE = `
#if defined( USE_COLOR_ALPHA )
	vColor = vec4( 1.0 );
#elif defined( USE_COLOR ) || defined( USE_INSTANCING_COLOR )
	vColor = vec3( 1.0 );
#endif
#ifdef USE_COLOR
	vColor *= color;
#endif
#ifdef USE_INSTANCING_COLOR
	vColor.xyz = blApplyStateColor( vColor.xyz, instanceColor.xyz );
#endif
`;

/**
 * Patch a material so its per-instance colour obeys the rule.
 * Idempotent, and it chains any onBeforeCompile already there - the
 * shading patch (surfaceShading.js) sits on the same materials, and
 * three's Material.copy() copies userData but NOT onBeforeCompile, so the
 * marker lives on the function itself.
 */
export function patchStateColor(material) {
  if (!material) return material;
  if (typeof material.onBeforeCompile === 'function' && material.onBeforeCompile.__blStateColor) return material;
  const previous = material.onBeforeCompile;
  const patched = function blStateColor(shader, renderer) {
    if (typeof previous === 'function') previous.call(this, shader, renderer);
    if (shader.vertexShader.indexOf('blApplyStateColor') !== -1) return;
    shader.vertexShader = GLSL_APPLY_STATE_COLOR + shader.vertexShader;
    shader.vertexShader = shader.vertexShader.replace(
      '#include <color_vertex>', COLOR_VERTEX_WITH_STATE,
    );
  };
  patched.__blStateColor = true;
  // CARRY EVERY OTHER PATCHER'S MARKER FORWARD.
  //
  // Each patcher guards by looking for ITS OWN marker on the installed
  // onBeforeCompile. Wrapping that function hides the marker underneath,
  // so the next call from the other patcher sees a function without its
  // flag and patches again - injecting its shader block a second time.
  // That is a redefinition, the program fails to LINK, and the instances
  // using it draw nothing at all: invisible, not wrong-coloured.
  //
  // It surfaced on a pool REGROW (GlobalInstancePool), which re-patches the
  // same material, so it only bit models big enough to outgrow a pool.
  //
  // Carrying the markers fixes the CLASS rather than this pair: a third
  // patcher added later composes safely without any of them knowing about
  // each other.
  if (typeof previous === 'function') {
    for (const k of Object.getOwnPropertyNames(previous)) {
      if (k.indexOf('__bl') === 0) patched[k] = previous[k];
    }
  }
  material.onBeforeCompile = patched;
  // Different program from an unpatched material of the same type.
  const prevKey = material.customProgramCacheKey;
  material.customProgramCacheKey = function blStateColorKey() {
    const base = typeof prevKey === 'function' ? prevKey.call(this) : '';
    return `${base}|blState1`;
  };
  material.needsUpdate = true;
  return material;
}
